import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from "../../AuthContext";
import { getAuth, updateProfile } from 'firebase/auth';
import { db, storage } from "../../firebase";
import { doc, collection, query, where, getDocs, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { HiOutlineUser, HiOutlineMail, HiOutlinePhone, HiOutlinePencilAlt, HiOutlineSave, HiOutlineShoppingBag, HiOutlineClock, HiOutlineBadgeCheck } from 'react-icons/hi';
import Footer from '../../Components/Footer/Footer';
import MainNav from '../../Components/Navbar/NewNavbar';
import Loading1 from "../../Components/Loading/Loading1";

function Profile() {
    const { user } = useContext(AuthContext);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newName, setNewName] = useState(user?.displayName || '');
    const [newEmail, setNewEmail] = useState(user?.email || '');
    const [newProfilePic, setNewProfilePic] = useState(user?.photoURL || '');
    const [profilePicFile, setProfilePicFile] = useState(null);
    const [editing, setEditing] = useState(false);
    const [isLoadingPage, setIsLoadingPage] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoadingPage(false);
        }, 1200);

        const fetchOrders = async () => {
            try {
                if (user) {
                    const ordersQuery = query(collection(db, "orders"), where("userId", "==", user.uid));
                    const querySnapshot = await getDocs(ordersQuery);
                    const ordersList = querySnapshot.docs.map(doc => doc.data());
                    setOrders(ordersList);
                }
                setLoading(false);
            } catch (error) {
                console.error("Error fetching orders: ", error);
            }
        };

        fetchOrders();
    }, [user]);

    const handleProfileUpdate = async () => {
        if (user) {
            try {
                let profilePicUrl = newProfilePic;
                if (profilePicFile) {
                    const storageRef = ref(storage, `profilePictures/${user.uid}`);
                    await uploadBytes(storageRef, profilePicFile);
                    profilePicUrl = await getDownloadURL(storageRef);
                    setNewProfilePic(profilePicUrl);
                }

                await updateProfile(user, { displayName: newName, photoURL: profilePicUrl });
                const userDoc = doc(db, "users", user.uid);
                await updateDoc(userDoc, {
                    displayName: newName,
                    email: newEmail,
                    photoURL: profilePicUrl,
                });

                setEditing(false);
                alert('Profile updated successfully');
            } catch (error) {
                console.error('Error updating profile:', error);
            }
        }
    };

    if (isLoadingPage) {
        return <Loading1 />;
    }

    return (
        <div className="bg-white min-h-screen">
            <MainNav />
            
            <section className="pt-40 pb-20 bg-secondary relative overflow-hidden">
                <div className="container-custom relative z-10 flex flex-col md:flex-row items-center gap-10">
                    <div className="relative group">
                        <div className="w-32 h-32 rounded-full border-4 border-white shadow-xl overflow-hidden bg-primary/10">
                            {newProfilePic ? (
                                <img src={newProfilePic} alt="Profile" className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-primary text-4xl font-black">
                                    {newName?.[0]}
                                </div>
                            )}
                        </div>
                        {editing && (
                            <label className="absolute bottom-0 right-0 w-10 h-10 bg-black text-white rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:bg-primary transition-colors">
                                <HiOutlinePencilAlt size={18} />
                                <input type="file" className="hidden" onChange={(e) => setProfilePicFile(e.target.files[0])} />
                            </label>
                        )}
                    </div>
                    
                    <div className="text-center md:text-left space-y-2">
                        <h1 className="text-4xl md:text-5xl font-heading font-black text-foreground">
                            {user?.displayName || "Member"} <span className="text-primary italic">Astra</span>
                        </h1>
                        <p className="text-muted-foreground font-medium flex items-center justify-center md:justify-start gap-2">
                            <HiOutlineBadgeCheck className="text-primary" />
                            Premium Hospitality Member
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-20">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-12 gap-12">
                        {/* Profile Details */}
                        <div className="lg:col-span-4 space-y-8">
                            <div className="card-premium p-10 space-y-8 border-t-8 border-t-primary">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-xl font-black">Account <span className="text-primary italic">Details</span></h2>
                                    <button 
                                        onClick={() => editing ? handleProfileUpdate() : setEditing(true)}
                                        className="text-xs font-black uppercase tracking-widest text-primary flex items-center gap-2"
                                    >
                                        {editing ? <><HiOutlineSave size={16} /> Save</> : <><HiOutlinePencilAlt size={16} /> Edit</>}
                                    </button>
                                </div>

                                <div className="space-y-6">
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Full Name</label>
                                        <div className="flex items-center gap-3 p-4 rounded-xl bg-secondary/50 border border-border">
                                            <HiOutlineUser className="text-primary" />
                                            {editing ? (
                                                <input type="text" value={newName} onChange={e => setNewName(e.target.value)} className="bg-transparent border-none outline-none w-full text-sm font-bold" />
                                            ) : (
                                                <span className="text-sm font-bold">{user?.displayName || "Not specified"}</span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="space-y-1">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Email Address</label>
                                        <div className="flex items-center gap-3 p-4 rounded-xl bg-secondary/50 border border-border">
                                            <HiOutlineMail className="text-primary" />
                                            <span className="text-sm font-bold text-muted-foreground">{user?.email}</span>
                                        </div>
                                    </div>

                                    <div className="space-y-1">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Phone Connection</label>
                                        <div className="flex items-center gap-3 p-4 rounded-xl bg-secondary/50 border border-border">
                                            <HiOutlinePhone className="text-primary" />
                                            <span className="text-sm font-bold">{user?.phoneNumber || "+91 Contact Pending"}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Orders List */}
                        <div className="lg:col-span-8 space-y-8">
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-black">Booking <span className="text-primary italic">History</span></h2>
                                <span className="px-4 py-2 rounded-full bg-primary/10 text-primary text-xs font-black uppercase tracking-widest">
                                    {orders.length} Records
                                </span>
                            </div>

                            {loading ? (
                                <div className="space-y-4">
                                    {[...Array(3)].map((_, i) => <div key={i} className="h-24 w-full bg-secondary animate-pulse rounded-3xl" />)}
                                </div>
                            ) : orders.length > 0 ? (
                                <div className="grid gap-6">
                                    {orders.map((order, index) => (
                                        <div key={index} className="card-premium p-8 hover:shadow-xl transition-all group overflow-hidden relative">
                                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
                                                <div className="flex items-center gap-6">
                                                    <div className="w-14 h-14 rounded-2xl bg-black text-white flex items-center justify-center shrink-0">
                                                        <HiOutlineShoppingBag size={24} />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <h4 className="font-bold text-lg">{order.service?.[0]?.name || "Cleaning Service"}</h4>
                                                        <div className="flex items-center gap-4 text-xs font-black uppercase tracking-widest text-muted-foreground">
                                                            <span className="flex items-center gap-1"><HiOutlineClock /> {order.serviceDate}</span>
                                                            <span className="flex items-center gap-1">{order.serviceTime}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between md:justify-end gap-8 border-t md:border-t-0 pt-4 md:pt-0 border-border/50">
                                                    <div className="text-right">
                                                        <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">Total Paid</div>
                                                        <div className="text-xl font-black text-primary">₹{order.amount}</div>
                                                    </div>
                                                    <span className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest ${order.status === 'Completed' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
                                                        {order.status}
                                                    </span>
                                                </div>
                                            </div>
                                            {/* Decorative pattern */}
                                            <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-primary/5 rounded-full flex items-center justify-center text-primary group-hover:scale-150 transition-transform duration-700">
                                                <HiOutlineClock size={40} className="opacity-10" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="card-premium py-20 text-center space-y-4 opacity-60">
                                    <HiOutlineShoppingBag size={64} className="mx-auto text-muted-foreground" />
                                    <p className="font-black uppercase tracking-widest text-xs">No bookings witnessed in your vault.</p>
                                    <button className="btn-primary !px-8 !py-3">Initiate First Booking</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

export default Profile;

